import StoreWrapper from 'connect/StoreWrapper';

/**
 * A Syncer manages translation from Apollo/GraphQL to Flux/Redux.
 *
 * It listens to and can access data just like DataConnectors:
 *
 *    class Syncer ... {
 *      stores = [AssayStore];
 *      shouldSubscribeToRedux = false;
 *
 *      readFromStore() {
 *         return this.assayStore().assaySchemasById;
 *      }
 *    }
 *
 * Flux/Redux models may slightly differ from GraphQL models, e.g. the older variant may just have a schemaId
 * field, but GraphQL prefers to return a schema object with a single id field.
 */
export default abstract class Syncer<TInputs, TGraphQL> extends StoreWrapper {
  /**
   * Read the most direct store representation available.
   *
   * We cache the last return value and rely on deep equality to determine whether to trigger a sync to Apollo
   *
   * @returns An object/list of all known instances (current), as they exist in the store,
   * along with a raw store reference for fast equality (equalityRef)
   */
  abstract readFromStore(): {current: TInputs; equalityRef: unknown} | void;

  /**
   * Given the result of readFromStore(), write the models to Apollo's cache.
   *
   * @param inputs Direct store representation, returned from readFromStore()
   */
  abstract writeToApolloCache(inputs: TInputs): void;

  /**
   * Write GraphQL models returned from Apollo into the store.
   *
   * For now, very few guarantees are made about the given list. A few potentially unexpected things that
   * should be accounted for:
   *
   * - It may contain duplicates if an object is returned multiple times by the query
   * - Multiple queries may request the same singleton collection (e.g. the list of all tag schemas), and
   *   those could be batched into this call
   *
   * @param graphQLObjs A list of GraphQL objs in the shape of FRAGMENT
   */
  abstract writeToStore(graphQLObjs: Array<TGraphQL>): void;
}
